/**
 * @fileoverview Hook global que dispara notificações do navegador para o
 * Chat Interno (DMs e menções @você). Mensagens em canais normais não
 * disparam popup — só atualizam o badge da sidebar (MainListItems).
 *
 * Deve ser usado no LoggedInLayout, que sempre monta para usuários
 * autenticados — assim o popup funciona mesmo quando o user NÃO está em
 * /internal-chat (ex: trabalhando em /tickets).
 *
 * Anti-spam: só notifica quando a aba está escondida (document.visibilityState
 * !== "visible"). Se o user já está olhando o app, o badge da sidebar e o
 * toast in-app dão a indicação visual sem precisar de popup.
 */

import { useContext, useEffect, useRef } from "react";
import { useHistory } from "react-router-dom";
import api from "../../services/api";
import { AuthContext } from "../../context/Auth/AuthContext";
import { SocketContext } from "../../context/Socket/SocketContext";

const ensurePermission = () => {
  if (typeof Notification === "undefined") return;
  if (Notification.permission === "default") {
    Notification.requestPermission().catch(() => {});
  }
};

const canShow = () => {
  if (typeof Notification === "undefined") return false;
  if (Notification.permission !== "granted") return false;
  // Não notifica se a aba está visível — o user já está no app.
  if (document.visibilityState === "visible") return false;
  return true;
};

const useInternalChatNotifications = () => {
  const { user } = useContext(AuthContext);
  const socketManager = useContext(SocketContext);
  const history = useHistory();
  const historyRef = useRef(history);
  historyRef.current = history;

  // Flag por-empresa que liga/desliga notificação do Chat Interno.
  // Default true se o setting não existir (retrocompat).
  const enabledRef = useRef(true);

  useEffect(() => {
    if (!user?.id || !user?.companyId) return undefined;
    if (user?.accessScope === "attendance") return undefined; // sem acesso ao chat

    ensurePermission();

    let cancelled = false;
    const loadSetting = async () => {
      try {
        const { data } = await api.get("/settings");
        if (cancelled) return;
        const s = Array.isArray(data)
          ? data.find((x) => x.key === "internalChatNotifications")
          : null;
        // Apenas "disabled" desativa — qualquer outro valor (inclusive ausente)
        // mantém ativo por default.
        enabledRef.current = s ? s.value !== "disabled" : true;
      } catch {
        enabledRef.current = true;
      }
    };
    loadSetting();

    const socket = socketManager.getSocket(user.companyId);
    const userEvent = `user-${user.id}`;
    // Quando admin troca o setting em /settings, o backend emite
    // company-${companyId}-settings — re-lemos para refletir na hora.
    const settingsEvent = `company-${user.companyId}-settings`;
    const handleSettingsChange = (data) => {
      if (data?.key === "internalChatNotifications") {
        enabledRef.current = data.value !== "disabled";
      }
    };
    socket.on(settingsEvent, handleSettingsChange);

    const handler = (data) => {
      if (!data || !canShow() || !enabledRef.current) return;

      // DM ou Group DM — qualquer mensagem disparada por outra pessoa.
      if (
        data.action === "newInternalMessage" &&
        (data.channelType === "dm" || data.channelType === "group_dm")
      ) {
        const sender = data?.message?.sender;
        if (!sender || sender.id === user.id) return;
        const senderName = sender?.name || "Alguém";
        const senderDisplay = sender?.jobRole?.name
          ? `${senderName} · ${sender.jobRole.name}`
          : senderName;
        const body = data?.message?.body || "Nova mensagem";
        try {
          const n = new Notification(senderDisplay, {
            body: body.slice(0, 200),
            icon: "/favicon.ico",
            tag: `internal-dm-${data.channelId}`,
            renotify: true,
          });
          n.onclick = () => {
            window.focus();
            historyRef.current.push(`/internal-chat?channel=${data.channelId}`);
            n.close();
          };
        } catch {
          // ignore
        }
        return;
      }

      // Menção @você em canal — independe de ser DM.
      if (data.action === "internalChatMention") {
        const senderName = data.senderDisplayName || data.senderName || "Alguém";
        try {
          const n = new Notification(`${senderName} mencionou você`, {
            body: (data.body || "").slice(0, 200),
            icon: "/favicon.ico",
            tag: `internal-mention-${data.channelId}`,
            renotify: true,
          });
          n.onclick = () => {
            window.focus();
            if (data.channelId) {
              historyRef.current.push(`/internal-chat?channel=${data.channelId}`);
            }
            n.close();
          };
        } catch {
          // ignore
        }
      }
    };

    socket.on(userEvent, handler);
    return () => {
      cancelled = true;
      socket.off(userEvent, handler);
      socket.off(settingsEvent, handleSettingsChange);
    };
  }, [socketManager, user?.id, user?.companyId, user?.accessScope]);
};

export default useInternalChatNotifications;
